/**
 * Print Template Settings: how a tenant's receipts and invoices look, never what they say.
 *
 * Structured settings only — no HTML, CSS, script or template code anywhere. A template chooses
 * paper, margins, type size, which optional parts appear and where the verification QR sits; it can
 * never change a figure, a document number, the date or the QR's contents, and it can never hide the
 * grand total, the QR or its caption.
 */

export const PrintDocumentType = {
  RECEIPT: 'RECEIPT',
  INVOICE: 'INVOICE',
} as const;
export type PrintDocumentType = (typeof PrintDocumentType)[keyof typeof PrintDocumentType];
export const PRINT_DOCUMENT_TYPES: PrintDocumentType[] = ['RECEIPT', 'INVOICE'];

export const PrintPaperSize = {
  THERMAL_58: 'THERMAL_58',
  THERMAL_80: 'THERMAL_80',
  A5: 'A5',
  A4: 'A4',
} as const;
export type PrintPaperSize = (typeof PrintPaperSize)[keyof typeof PrintPaperSize];

/** Which paper each document may use: an invoice is the full fiscal table and needs a sheet. */
export const PRINT_PAPER_SIZES_BY_DOCUMENT: Record<PrintDocumentType, PrintPaperSize[]> = {
  RECEIPT: ['THERMAL_58', 'THERMAL_80', 'A5', 'A4'],
  INVOICE: ['A5', 'A4'],
};

/** Physical paper, in millimetres; a thermal roll has no fixed height. */
export const PRINT_PAPER: Record<
  PrintPaperSize,
  {
    widthMm: number;
    heightMm: number | null;
    kind: 'thermal' | 'sheet';
    /** The narrowest usable width the layout is readable at: document number, total and the QR block. */
    minUsableWidthMm: number;
    /** Base type size in CSS pixels, before the font scale. */
    baseFontPx: number;
  }
> = {
  THERMAL_58: {
    widthMm: 58,
    heightMm: null,
    kind: 'thermal',
    minUsableWidthMm: 44,
    baseFontPx: 10,
  },
  THERMAL_80: {
    widthMm: 80,
    heightMm: null,
    kind: 'thermal',
    minUsableWidthMm: 60,
    baseFontPx: 11,
  },
  A5: { widthMm: 148, heightMm: 210, kind: 'sheet', minUsableWidthMm: 110, baseFontPx: 11 },
  A4: { widthMm: 210, heightMm: 297, kind: 'sheet', minUsableWidthMm: 150, baseFontPx: 12 },
};

/** A sheet must keep at least this much height between its top and bottom margins. */
export const PRINT_MIN_USABLE_HEIGHT_MM = 120;
export const PRINT_MARGIN_MIN_MM = 0;
export const PRINT_MARGIN_MAX_MM = 30;

export const PrintFontScale = { SMALL: 'SMALL', MEDIUM: 'MEDIUM', LARGE: 'LARGE' } as const;
export type PrintFontScale = (typeof PrintFontScale)[keyof typeof PrintFontScale];
export const PRINT_FONT_SCALE_FACTOR: Record<PrintFontScale, number> = {
  SMALL: 0.9,
  MEDIUM: 1,
  LARGE: 1.15,
};

/** Left and right follow the reading direction: in Arabic, LEFT is the start of the line, on the right. */
export const PrintAlignment = { LEFT: 'LEFT', CENTER: 'CENTER', RIGHT: 'RIGHT' } as const;
export type PrintAlignment = (typeof PrintAlignment)[keyof typeof PrintAlignment];

export const PrintDensity = { COMPACT: 'COMPACT', COMFORTABLE: 'COMFORTABLE' } as const;
export type PrintDensity = (typeof PrintDensity)[keyof typeof PrintDensity];

export const PrintQrSize = { SMALL: 'SMALL', MEDIUM: 'MEDIUM', LARGE: 'LARGE' } as const;
export type PrintQrSize = (typeof PrintQrSize)[keyof typeof PrintQrSize];

/** The QR's physical width. 28 mm is the floor every preset keeps, so no choice can print it too small to scan. */
export const PRINT_QR_MIN_MM = 28;
export const PRINT_QR_SIZE_MM: Record<'thermal' | 'sheet', Record<PrintQrSize, number>> = {
  thermal: { SMALL: 28, MEDIUM: 32, LARGE: 36 },
  sheet: { SMALL: 30, MEDIUM: 35, LARGE: 40 },
};

/** Like alignment, left and right follow the reading direction. */
export const PrintQrPlacement = {
  FOOTER_LEFT: 'FOOTER_LEFT',
  FOOTER_CENTER: 'FOOTER_CENTER',
  FOOTER_RIGHT: 'FOOTER_RIGHT',
} as const;
export type PrintQrPlacement = (typeof PrintQrPlacement)[keyof typeof PrintQrPlacement];

export const PrintLogoSize = { SMALL: 'SMALL', MEDIUM: 'MEDIUM', LARGE: 'LARGE' } as const;
export type PrintLogoSize = (typeof PrintLogoSize)[keyof typeof PrintLogoSize];
/** The printed logo's height in millimetres; its width follows its own proportions, never wider than the column. */
export const PRINT_LOGO_HEIGHT_MM: Record<'thermal' | 'sheet', Record<PrintLogoSize, number>> = {
  thermal: { SMALL: 10, MEDIUM: 14, LARGE: 18 },
  sheet: { SMALL: 14, MEDIUM: 20, LARGE: 26 },
};

/** Plain-text footer bounds: characters and lines, per language. */
export const PRINT_FOOTER_MAX_CHARS = 300;
export const PRINT_FOOTER_MAX_LINES = 6;

/**
 * Everything a template decides. Logo and tax-registration controls are deliberately absent: no
 * tenant or branch holds a logo or a tax registration number yet, and a switch with nothing behind it
 * would mislead. Both are follow-ups that add fields here without reshaping anything.
 */
export interface PrintTemplate {
  paperSize: PrintPaperSize;
  marginTopMm: number;
  marginBottomMm: number;
  marginLeftMm: number;
  marginRightMm: number;
  fontScale: PrintFontScale;

  headerAlignment: PrintAlignment;
  showStoreName: boolean;
  showBranchName: boolean;
  showBranchAddress: boolean;
  showBranchPhone: boolean;
  /** Branding comes from the document's identity frozen at issuance, never from the tenant's current profile. */
  showLogo: boolean;
  logoSize: PrintLogoSize;
  showTaxRegistration: boolean;
  /** The registered legal name, printed in addition to (never instead of) the display name. */
  showLegalName: boolean;

  itemDensity: PrintDensity;
  showSku: boolean;
  showQuantity: boolean;
  showUnitPrice: boolean;
  /** Sheets only: a thermal roll has no room for a discount column. */
  showDiscount: boolean;
  /** Sheets only, for the same reason. */
  showTax: boolean;
  showLineTotal: boolean;

  showSubtotal: boolean;
  showDiscountTotal: boolean;
  /** Honoured only when the document's tax is zero: a document that charged tax always shows it. */
  showTaxTotal: boolean;
  showPaid: boolean;
  showOutstanding: boolean;

  qrSize: PrintQrSize;
  qrPlacement: PrintQrPlacement;

  footerEn: string;
  footerAr: string;
  returnPolicyEn: string;
  returnPolicyAr: string;
  showThankYou: boolean;
}

export type PrintTemplateField = keyof PrintTemplate;

const SHARED_DEFAULTS: Omit<
  PrintTemplate,
  'paperSize' | 'marginTopMm' | 'marginBottomMm' | 'marginLeftMm' | 'marginRightMm' | 'showSku'
> = {
  fontScale: 'MEDIUM',
  headerAlignment: 'CENTER',
  showStoreName: true,
  showBranchName: true,
  showBranchAddress: true,
  showBranchPhone: true,
  showLogo: false,
  logoSize: 'MEDIUM',
  showTaxRegistration: false,
  showLegalName: false,
  itemDensity: 'COMFORTABLE',
  showQuantity: true,
  showUnitPrice: true,
  showDiscount: true,
  showTax: true,
  showLineTotal: true,
  showSubtotal: true,
  showDiscountTotal: true,
  showTaxTotal: true,
  showPaid: true,
  showOutstanding: true,
  qrSize: 'SMALL',
  qrPlacement: 'FOOTER_CENTER',
  footerEn: '',
  footerAr: '',
  returnPolicyEn: '',
  returnPolicyAr: '',
  showThankYou: false,
};

/**
 * The system default for each document, which a tenant with no saved template prints with.
 *
 * Chosen to reproduce the approved layouts exactly: an 80 mm receipt whose 4 mm side margins leave
 * the same 72 mm column it always printed, and an A4 invoice whose 15 mm side margins leave the same
 * 180 mm, both with the 6 mm top and bottom the print stylesheet always used.
 */
export const DEFAULT_PRINT_TEMPLATES: Record<PrintDocumentType, PrintTemplate> = {
  RECEIPT: {
    ...SHARED_DEFAULTS,
    paperSize: 'THERMAL_80',
    marginTopMm: 6,
    marginBottomMm: 6,
    marginLeftMm: 4,
    marginRightMm: 4,
    showSku: false,
  },
  INVOICE: {
    ...SHARED_DEFAULTS,
    paperSize: 'A4',
    marginTopMm: 6,
    marginBottomMm: 6,
    marginLeftMm: 15,
    marginRightMm: 15,
    showSku: true,
  },
};

/** A branch's changes to the tenant template for one document type: only the fields it changed. */
export type PrintTemplateOverride = Partial<PrintTemplate>;

/** A branch's print template as the editor sees it: its own changes, the tenant template they sit on, and what that resolves to. `version` is 0 while the branch uses the tenant template. */
export interface BranchPrintTemplateView {
  documentType: PrintDocumentType;
  branchId: string;
  overrides: PrintTemplateOverride;
  tenantTemplate: PrintTemplate;
  resolved: PrintTemplate;
  version: number;
}

/** A document type's template as the editor sees it: what prints now, whether it is still the system default, and the version a save must name. */
export interface PrintTemplateView {
  documentType: PrintDocumentType;
  template: PrintTemplate;
  isDefault: boolean;
  version: number;
}

/** Why a template cannot be saved, one entry per problem; empty when it can. */
export interface PrintTemplateProblem {
  field: PrintTemplateField;
  code:
    | 'PAPER_NOT_ALLOWED'
    | 'MARGIN_OUT_OF_RANGE'
    | 'WIDTH_TOO_NARROW'
    | 'HEIGHT_TOO_SHORT'
    | 'QR_DOES_NOT_FIT'
    | 'TEXT_TOO_LONG'
    | 'TOO_MANY_LINES'
    | 'MARKUP_NOT_ALLOWED';
}
